import { OidcClient, UserManager, WebStorageStateStore, type User } from "oidc-client-ts";
import { redirectUri, type AppConfig } from "./config.js";
import { currentPath, sanitizeReturnTo, shouldAttemptReauth } from "./auth-recovery.js";

/**
 * Cognito Hosted UI login (authorization code + PKCE) against the RESTAURANT user pool.
 *
 * We use oidc-client-ts rather than hand-rolling PKCE/token handling. Tokens live in
 * sessionStorage so they don't outlive the browser session; there is no client secret
 * (a browser app cannot keep one).
 *
 * Built lazily because the pool/client come from runtime config, not the bundle.
 */
let manager: UserManager | undefined;

export function initAuth(config: AppConfig): void {
  manager = new UserManager({
    authority: config.cognitoAuthority,
    client_id: config.cognitoClientId,
    redirect_uri: redirectUri(),
    post_logout_redirect_uri: window.location.origin,
    response_type: "code",
    scope: "openid email profile",
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    automaticSilentRenew: true
  });
}

function mgr(): UserManager {
  if (!manager) throw new Error("auth not initialized — call initAuth(config) first");
  return manager;
}

/** Send the user to the Hosted UI to sign in. */
export const login = () => mgr().signinRedirect();

/**
 * Send the user straight to Cognito's REGISTRATION form.
 *
 * This used to pass `screen_hint=signup` to /oauth2/authorize, which is the documented way — and it does
 * nothing on this pool. The parameter survives the redirect to /login and is then ignored, because
 * screen_hint is a MANAGED LOGIN feature and this pool runs the classic Hosted UI. Measured rather than
 * assumed: /login is 9,003 bytes, /login with the hint is 9,095 (the parameter is merely echoed into the
 * markup), and /signup is a different 21,458-byte page. So "Create an account" landed everyone on the
 * sign-in form and asked them to find the small print link themselves.
 *
 * createSigninRequest builds the same authorize URL AND persists the PKCE verifier and state, so swapping
 * only the endpoint leaves a request the callback can still validate. Cognito redirects back to the same
 * redirect_uri with a code, exactly as sign-in does.
 */
export async function signup(): Promise<void> {
  /*
   * OidcClient rather than the UserManager, because createSigninRequest is not on the manager's public
   * surface. Constructed from the manager's OWN resolved settings, so the state store is the same object and
   * the verifier this writes is the one signinRedirectCallback later looks for. Building a client from
   * scratch here would rely on two sets of defaults staying in step.
   */
  const request = await new OidcClient(mgr().settings).createSigninRequest({
    redirect_uri: redirectUri()
  });
  /*
   * Only the path is replaced. Every query parameter — client_id, redirect_uri, scope, code_challenge —
   * has to survive untouched or the code that comes back cannot be exchanged.
   */
  window.location.assign(request.url.replace("/oauth2/authorize", "/signup"));
}
/**
 * Sign out of Cognito and come back to this console's root, which shows the sign-in card.
 *
 * COGNITO'S /logout DOES NOT READ post_logout_redirect_uri, the standard OIDC parameter oidc-client-ts sends
 * from the settings above. Measured against the live pool: given only that, /logout redirects to /login with
 * no redirect_uri, which ends on /error?error=Required+parameters+missing, "An error was encountered with the
 * requested page". Every sign-out did that. Cognito's own parameter is logout_uri, and it must be one of the
 * app client's sign-out URLs (the profile stack registers each console origin).
 *
 * client_id IS SENT EXPLICITLY TOO. oidc-client-ts adds client_id only when it has no ID token to send, and a
 * signed-in user always has one, so the URL went out with id_token_hint and no client_id. Cognito's /logout
 * needs client_id and ended on "Client does not exist". Measured on the beta pool: without it, 400 at /error;
 * with it, 302 to the console. When the library does add its own (no ID token), the parameter appears twice,
 * which Cognito accepts: also measured.
 *
 * The root is safe to land on signed out: only /r/ paths start a sign-in by themselves, so there is no loop.
 */
export const logout = () =>
  mgr().signoutRedirect(signoutArgs(mgr().settings.client_id, window.location.origin));

/** The sign-out parameters Cognito reads. Exported so a test can run them through the real library. */
export function signoutArgs(clientId: string, origin: string): {
  extraQueryParams: { client_id: string; logout_uri: string };
} {
  return { extraQueryParams: { client_id: clientId, logout_uri: origin } };
}
export const getUser = () => mgr().getUser();

/** Where a re-auth attempt is recorded, to break redirect loops across page loads. */
const REAUTH_MARKER = "glowin.reauth.at";

/**
 * Complete the Hosted UI redirect, returning the in-app path to resume.
 *
 * `state` carries where the user was when their session expired, so an expiry mid-task
 * doesn't dump them back at the top. It is sanitised because it round-trips through the
 * identity provider.
 */
export async function completeLogin(): Promise<string> {
  const user = await mgr().signinRedirectCallback();
  window.sessionStorage.removeItem(REAUTH_MARKER); // sign-in worked; re-arm the guard
  return sanitizeReturnTo(user.state);
}

/**
 * The user, but only if their token is still usable.
 *
 * getUser() returns whatever is in storage including an EXPIRED session, which is how the
 * console used to render a full authenticated shell whose every request 401'd. Tries a
 * silent renew before giving up, so a long-open tab recovers by itself.
 */
export async function getActiveUser(): Promise<User | null> {
  const user = await mgr().getUser();
  if (user && !user.expired) return user;
  if (!user) return null;
  return renewSilently();
}

async function renewSilently(): Promise<User | null> {
  try {
    return await mgr().signinSilent();
  } catch {
    return null; // refresh token gone or rejected — a full sign-in is needed
  }
}

/** A currently-valid ID token, renewing first if necessary. */
export async function currentToken(): Promise<string | undefined> {
  return bearerToken(await getActiveUser());
}

/** Force a renew after the API rejected our token, even if it looked unexpired. */
export async function renewToken(): Promise<string | undefined> {
  return bearerToken(await renewSilently());
}

/**
 * Send the user back through sign-in, remembering where they were.
 *
 * Returns false when it declines to act because we just tried — signing in evidently
 * didn't fix the rejection, so surfacing the error beats an endless redirect loop.
 */
export async function recoverSession(now = Date.now()): Promise<boolean> {
  const raw = window.sessionStorage.getItem(REAUTH_MARKER);
  const last = raw === null ? undefined : Number(raw);
  if (!shouldAttemptReauth(last, now)) return false;

  window.sessionStorage.setItem(REAUTH_MARKER, String(now));
  await mgr().signinRedirect({ state: currentPath(window.location) });
  return true;
}

/**
 * We send the ID TOKEN, not the access token: Cognito access tokens carry no `email`
 * claim, and the API authorizes operators by verified email domain.
 */
export function bearerToken(user: User | null): string | undefined {
  return user?.id_token;
}

export interface CallerInfo {
  email?: string;
}

/**
 * Identity for display only. The restaurant list is NOT in the token — it comes from
 * GET /restaurants, backed by the membership table, so revoking access takes effect
 * immediately instead of when this token expires.
 */
export function callerInfo(user: User | null): CallerInfo {
  const claims = (user?.profile ?? {}) as Record<string, unknown>;
  return {
    email: typeof claims.email === "string" ? claims.email : undefined
  };
}
